/**
 * Simple State Machine - WordPress compatible
 * Provides basic state management for UI components
 */
(function (global) {
  "use strict";

  /**
   * Creates a simple state machine
   * @param {Object} config - Configuration object with states and transitions
   * @returns {Object} State machine instance
   */
  function createStateMachine(config = {}) {
    const {
      initialState = "idle",
      states = {},
      transitions = {},
      onStateChange = null,
    } = config;

    let currentState = initialState;
    const listeners = [];

    const machine = {
      // Get current state
      getState() {
        return currentState;
      },

      // Check if in specific state
      is(state) {
        return currentState === state;
      },

      // Transition to new state
      transition(newState, data = {}) {
        const oldState = currentState;

        // Check if transition is valid
        if (
          transitions[currentState] &&
          !transitions[currentState].includes(newState)
        ) {
          console.warn(
            `Invalid transition from ${currentState} to ${newState}`
          );
          return false;
        }

        // Check if new state exists
        if (states[newState] === undefined) {
          console.warn(`State ${newState} does not exist`);
          return false;
        }

        // Exit current state
        if (
          states[currentState] &&
          typeof states[currentState].exit === "function"
        ) {
          states[currentState].exit(data);
        }

        // Change state
        currentState = newState;

        // Enter new state
        if (
          states[currentState] &&
          typeof states[currentState].enter === "function"
        ) {
          states[currentState].enter(data);
        }

        // Notify listeners
        listeners.forEach((listener) => {
          if (typeof listener === "function") {
            listener(newState, oldState, data);
          }
        });

        // Call global state change handler
        if (typeof onStateChange === "function") {
          onStateChange(newState, oldState, data);
        }

        // Trigger custom event
        if (typeof CustomEvent !== "undefined") {
          const stateChangeEvent = new CustomEvent("statemachine:change", {
            detail: { newState, oldState, data },
          });
          document.dispatchEvent(stateChangeEvent);
        }

        // jQuery compatibility
        if (typeof jQuery !== "undefined" && jQuery.fn.trigger) {
          jQuery(document).trigger("statemachine:change", [
            newState,
            oldState,
            data,
          ]);
        }

        return true;
      },

      // Add state change listener
      onStateChange(listener) {
        if (typeof listener === "function") {
          listeners.push(listener);
        }
        return () => {
          const index = listeners.indexOf(listener);
          if (index > -1) {
            listeners.splice(index, 1);
          }
        };
      },

      // Get all available states
      getStates() {
        return Object.keys(states);
      },

      // Get valid transitions from current state
      getValidTransitions() {
        return transitions[currentState] || [];
      },
    };

    return machine;
  }

  // Example usage and common state machines
  const createUIStateMachine = () => {
    return createStateMachine({
      initialState: "idle",
      states: {
        idle: {
          enter: () => console.log("UI is idle"),
          exit: () => console.log("Leaving idle state"),
        },
        loading: {
          enter: () => {
            console.log("UI is loading");
            document.body.classList.add("is-loading");
          },
          exit: () => {
            document.body.classList.remove("is-loading");
          },
        },
        active: {
          enter: () => {
            console.log("UI is active");
            document.body.classList.add("is-active");
          },
          exit: () => {
            document.body.classList.remove("is-active");
          },
        },
        error: {
          enter: (data) => {
            console.log("UI error state", data);
            document.body.classList.add("has-error");
          },
          exit: () => {
            document.body.classList.remove("has-error");
          },
        },
      },
      transitions: {
        idle: ["loading", "active"],
        loading: ["active", "error", "idle"],
        active: ["loading", "idle"],
        error: ["idle"],
      },
    });
  };

  // Make available globally
  global.createStateMachine = createStateMachine;
  global.createUIStateMachine = createUIStateMachine;

  // CommonJS compatibility
  if (typeof module !== "undefined" && module.exports) {
    module.exports = {
      createStateMachine,
      createUIStateMachine,
    };
  }
})(typeof window !== "undefined" ? window : this);
